import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import SpecialiteSelect from '@/components/feature/SpecialiteSelect';

interface PersonnelData {
  id: string;
  user_id: string;
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  sexe?: string;
  specialite?: string;
  numero_ordre?: string;
  bloque?: boolean;
}

interface RendezVous {
  id: string;
  patient_id: string;
  date_rdv: string;
  heure_debut: string;
  heure_fin: string;
  statut: string;
  motif?: string;
  type_rdv?: string;
  patients?: { nom: string; prenom: string; numero_dossier?: string } | null;
}

interface Disponibilite {
  id: string;
  medecin_id: string;
  jour_semaine: string;
  heure_debut: string;
  heure_fin: string;
  actif: boolean;
}

const JOURS_SEMAINE = [
  { value: 'lundi', label: 'Lundi' },
  { value: 'mardi', label: 'Mardi' },
  { value: 'mercredi', label: 'Mercredi' },
  { value: 'jeudi', label: 'Jeudi' },
  { value: 'vendredi', label: 'Vendredi' },
  { value: 'samedi', label: 'Samedi' },
  { value: 'dimanche', label: 'Dimanche' },
];

const STATUT_LABELS: Record<string, string> = {
  en_attente: 'En attente',
  confirme: 'Confirmé',
  en_cours: 'En cours',
  termine: 'Terminé',
  annule: 'Annulé',
  absent: 'Absent',
};

const STATUT_COLORS: Record<string, string> = {
  en_attente: 'bg-yellow-100 text-yellow-700',
  confirme: 'bg-blue-100 text-blue-700',
  en_cours: 'bg-purple-100 text-purple-700',
  termine: 'bg-green-100 text-green-700',
  annule: 'bg-red-100 text-red-700',
  absent: 'bg-gray-100 text-gray-700',
};

type RoleType = 'medecin' | 'secretaire' | 'sage_femme' | 'laborantin' | 'comptable';

interface GererPersonnelModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: RoleType;
  personnel: PersonnelData;
  onUpdate: () => void;
}

export default function GererPersonnelModal({ isOpen, onClose, type, personnel, onUpdate }: GererPersonnelModalProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'rdv' | 'dispos'>('info');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Password change
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loadingPassword, setLoadingPassword] = useState(false);

  // Info form
  const [formData, setFormData] = useState({ ...personnel });

  // RDV
  const [rendezVous, setRendezVous] = useState<RendezVous[]>([]);
  const [loadingRdv, setLoadingRdv] = useState(false);

  // Dispos
  const [disponibilites, setDisponibilites] = useState<Disponibilite[]>([]);
  const [loadingDispos, setLoadingDispos] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({ ...personnel });
      setActiveTab('info');
      setSuccess('');
      setError('');
      setShowPasswordSection(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setPasswordSuccess('');
      if (type === 'medecin') {
        loadRendezVous();
        loadDisponibilites();
      }
    }
  }, [isOpen, personnel.id, type]);

  const tableName = type === 'medecin' ? 'medecins' : type === 'secretaire' ? 'secretaires' : type === 'sage_femme' ? 'sage_femmes' : type === 'laborantin' ? 'laborantins' : 'comptables';

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!newPassword || newPassword.length < 10) {
      setPasswordError('Le mot de passe doit contenir au moins 10 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoadingPassword(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        setPasswordError('Session expirée, veuillez vous reconnecter');
        setLoadingPassword(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const fnUrl = `${supabaseUrl}/functions/v1/admin-reset-password`;

      const response = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ userId: personnel.user_id, newPassword }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        setPasswordError(result?.error || `Erreur serveur (${response.status})`);
      } else {
        setPasswordSuccess('Mot de passe changé avec succès !');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setPasswordSuccess('');
          setShowPasswordSection(false);
        }, 2500);
      }
    } catch (err: any) {
      setPasswordError(err.message || 'Erreur inattendue');
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleSaveInfo = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updates: Record<string, any> = {};
      const editableFields = getEditableFields();

      for (const field of editableFields) {
        const currentVal = formData[field as keyof typeof formData];
        const originalVal = personnel[field as keyof typeof personnel];
        // Normaliser : undefined, null, et '' sont tous traités comme null
        const normalizedCurrent = (currentVal === '' || currentVal === undefined || currentVal === null) ? null : currentVal;
        const normalizedOriginal = (originalVal === '' || originalVal === undefined || originalVal === null) ? null : originalVal;

        if (normalizedCurrent !== normalizedOriginal) {
          updates[field] = normalizedCurrent;
        }
      }

      if (Object.keys(updates).length === 0) {
        setSuccess('Aucune modification détectée');
        setLoading(false);
        return;
      }

      const { error: updateError, data: updatedData } = await supabase
        .from(tableName)
        .update(updates)
        .eq('id', personnel.id)
        .select('id')
        .single();

      if (updateError) {
        setError('Erreur lors de la mise à jour : ' + updateError.message);
      } else if (!updatedData) {
        setError('La modification a été bloquée. Vérifiez vos droits d\'accès. (Aucune ligne modifiée)');
      } else {
        if (updates.email) {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;
          if (token) {
            const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
            const fnUrl = `${supabaseUrl}/functions/v1/admin-update-user-email`;

            const authResponse = await fetch(fnUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                userId: personnel.user_id,
                newEmail: updates.email,
                tableName,
                recordId: personnel.id,
              }),
            });

            const authResult = await authResponse.json();

            if (!authResponse.ok || !authResult?.success) {
              setSuccess('Infos mises à jour, mais synchro email de connexion échouée : ' + (authResult?.error || 'erreur inconnue'));
            } else {
              setSuccess('Informations mises à jour avec succès ! (email de connexion synchronisé)');
            }
          } else {
            setSuccess('Informations mises à jour ! (email de connexion non synchronisé - session expirée)');
          }
        } else {
          setSuccess('Informations mises à jour avec succès !');
        }
        onUpdate();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err: any) {
      console.error('Erreur handleSaveInfo:', err);
      setError('Erreur inattendue : ' + (err?.message || 'Une erreur est survenue'));
    } finally {
      setLoading(false);
    }
  };

  const getEditableFields = (): string[] => {
    const base = ['nom', 'prenom', 'sexe', 'email', 'telephone'];
    if (type === 'medecin') return [...base, 'specialite', 'numero_ordre'];
    if (type === 'laborantin') return [...base, 'specialite'];
    return base;
  };

  // --- RDV ---
  const loadRendezVous = async () => {
    setLoadingRdv(true);
    const { data, error: rdvError } = await supabase
      .from('rendez_vous')
      .select('id, patient_id, date_rdv, heure_debut, heure_fin, statut, motif, type_rdv, patients(nom, prenom, numero_dossier)')
      .eq('medecin_id', personnel.id)
      .order('date_rdv', { ascending: false })
      .limit(50);

    if (!rdvError && data) {
      setRendezVous(data as RendezVous[]);
    }
    setLoadingRdv(false);
  };

  const handleCancelRdv = async (rdvId: string) => {
    if (!confirm('Annuler ce rendez-vous ?')) return;
    const { error: cancelError } = await supabase
      .from('rendez_vous')
      .update({ statut: 'annule' })
      .eq('id', rdvId);

    if (!cancelError) {
      setRendezVous(prev => prev.map(r => r.id === rdvId ? { ...r, statut: 'annule' } : r));
    }
  };

  // --- Dispos ---
  const loadDisponibilites = useCallback(async () => {
    setLoadingDispos(true);
    const { data, error: dispoError } = await supabase
      .from('disponibilites_medecins')
      .select('*')
      .eq('medecin_id', personnel.id)
      .order('jour_semaine');

    if (!dispoError && data) {
      setDisponibilites(data as Disponibilite[]);
    }
    setLoadingDispos(false);
  }, [personnel.id]);

  const handleToggleDispo = async (dispoId: string, currentStatus: boolean) => {
    const { error: updError } = await supabase
      .from('disponibilites_medecins')
      .update({ actif: !currentStatus })
      .eq('id', dispoId);

    if (!updError) {
      setDisponibilites(prev => prev.map(d => d.id === dispoId ? { ...d, actif: !currentStatus } : d));
    }
  };

  const handleUpdateDispoHeure = async (dispoId: string, field: 'heure_debut' | 'heure_fin', value: string) => {
    const { error: updError } = await supabase
      .from('disponibilites_medecins')
      .update({ [field]: value })
      .eq('id', dispoId);

    if (!updError) {
      setDisponibilites(prev => prev.map(d => d.id === dispoId ? { ...d, [field]: value } : d));
    }
  };

  const handleAddDispo = async (jour: string) => {
    const { data, error: insError } = await supabase
      .from('disponibilites_medecins')
      .insert({
        medecin_id: personnel.id,
        jour_semaine: jour,
        heure_debut: '09:00',
        heure_fin: '17:00',
        actif: true,
      })
      .select()
      .single();

    if (!insError && data) {
      setDisponibilites(prev => [...prev, data as Disponibilite]);
    }
  };

  const handleDeleteDispo = async (dispoId: string) => {
    if (!confirm('Supprimer ce créneau ?')) return;
    const { error: delError } = await supabase
      .from('disponibilites_medecins')
      .delete()
      .eq('id', dispoId);

    if (!delError) {
      setDisponibilites(prev => prev.filter(d => d.id !== dispoId));
    }
  };

  const getDisposParJour = (jour: string) => disponibilites.filter(d => d.jour_semaine === jour);

  const roleLabels: Record<RoleType, string> = {
    medecin: 'Médecin',
    secretaire: 'Secrétaire',
    sage_femme: 'Sage-femme',
    laborantin: 'Laborantin',
    comptable: 'Comptable',
  };

  const roleIcons: Record<RoleType, string> = {
    medecin: 'ri-stethoscope-line',
    secretaire: 'ri-user-settings-line',
    sage_femme: 'ri-nurse-line',
    laborantin: 'ri-microscope-line',
    comptable: 'ri-calculator-line',
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-10 pb-10 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl mx-4 z-10 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b-2 border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-clinic-blue to-clinic-blue-dark rounded-2xl flex items-center justify-center">
              <i className={`${roleIcons[type]} text-2xl text-white`}></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-clinic-blue-dark">
                {type === 'medecin' ? 'Dr ' : ''}{personnel.nom} {personnel.prenom}
              </h2>
              <p className="text-sm text-gray-500">
                {roleLabels[type]}{personnel.specialite ? ` · ${personnel.specialite}` : ''}
                {personnel.bloque && <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">Bloqué</span>}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-5 py-2.5 rounded-t-xl font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'info'
                  ? 'bg-white text-clinic-blue-dark border-x border-t border-gray-200 -mb-px'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className="ri-user-line mr-1.5"></i>Informations
            </button>
            {type === 'medecin' && (
              <>
                <button
                  onClick={() => setActiveTab('rdv')}
                  className={`px-5 py-2.5 rounded-t-xl font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === 'rdv'
                      ? 'bg-white text-clinic-blue-dark border-x border-t border-gray-200 -mb-px'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-calendar-check-line mr-1.5"></i>Rendez-vous
                </button>
                <button
                  onClick={() => setActiveTab('dispos')}
                  className={`px-5 py-2.5 rounded-t-xl font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === 'dispos'
                      ? 'bg-white text-clinic-blue-dark border-x border-t border-gray-200 -mb-px'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-time-line mr-1.5"></i>Disponibilités
                </button>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm flex items-start">
              <i className="ri-check-line mr-2 mt-0.5"></i>{success}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm flex items-start">
              <i className="ri-error-warning-line mr-2 mt-0.5"></i>{error}
            </div>
          )}

          {/* Tab: Info */}
          {activeTab === 'info' && (
            <div className="space-y-5">
              <h3 className="text-lg font-semibold text-gray-800">Modifier les informations</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Prénom</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sexe</label>
                <select
                  value={formData.sexe || ''}
                  onChange={(e) => setFormData({ ...formData, sexe: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm pr-8"
                >
                  <option value="">Non renseigné</option>
                  <option value="Masculin">Masculin</option>
                  <option value="Féminin">Féminin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone || ''}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>

              {(type === 'medecin' || type === 'laborantin') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Spécialité</label>
                  <SpecialiteSelect
                    value={formData.specialite || ''}
                    onChange={(val) => setFormData({ ...formData, specialite: val })}
                    placeholder="Tapez ou sélectionnez une spécialité..."
                  />
                </div>
              )}

              {type === 'medecin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Numéro d'ordre</label>
                  <input
                    type="text"
                    value={formData.numero_ordre || ''}
                    onChange={(e) => setFormData({ ...formData, numero_ordre: e.target.value })}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                  />
                </div>
              )}

              <div className="pt-4 flex gap-3">
                <button
                  onClick={handleSaveInfo}
                  disabled={loading}
                  className="px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin mr-2"></i>Enregistrement...</>
                  ) : (
                    <><i className="ri-save-line mr-2"></i>Enregistrer les modifications</>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-all font-semibold whitespace-nowrap cursor-pointer"
                >
                  Fermer
                </button>
              </div>

              {/* Section Changement de mot de passe */}
              <div className="mt-6 pt-6 border-t-2 border-gray-100">
                {!showPasswordSection ? (
                  <button
                    onClick={() => setShowPasswordSection(true)}
                    className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-2 border-amber-200 rounded-xl text-amber-700 hover:bg-amber-100 transition-all cursor-pointer w-full"
                  >
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-lock-password-line text-xl text-amber-600"></i>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-sm">Changer le mot de passe</div>
                      <div className="text-xs text-amber-600">Définir un nouveau mot de passe pour cet utilisateur</div>
                    </div>
                    <i className="ri-arrow-right-s-line text-xl ml-auto"></i>
                  </button>
                ) : (
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                        <i className="ri-lock-password-line text-lg"></i>
                        Changer le mot de passe
                      </h4>
                      <button
                        onClick={() => {
                          setShowPasswordSection(false);
                          setNewPassword('');
                          setConfirmPassword('');
                          setPasswordError('');
                        }}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-amber-200/50 text-amber-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>

                    {passwordError && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm flex items-start">
                        <i className="ri-error-warning-line mr-2 mt-0.5 flex-shrink-0"></i>
                        <span>{passwordError}</span>
                      </div>
                    )}
                    {passwordSuccess && (
                      <div className="mb-3 p-3 bg-green-50 border border-green-200 text-green-600 rounded-xl text-sm flex items-start">
                        <i className="ri-check-line mr-2 mt-0.5 flex-shrink-0"></i>
                        <span>{passwordSuccess}</span>
                      </div>
                    )}

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-amber-800 mb-1.5">
                          Nouveau mot de passe
                        </label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-2.5 border-2 border-amber-200 rounded-xl focus:outline-none focus:border-amber-400 bg-white text-sm"
                          placeholder="Minimum 10 caractères"
                          minLength={10}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-amber-800 mb-1.5">
                          Confirmer le mot de passe
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-4 py-2.5 border-2 border-amber-200 rounded-xl focus:outline-none focus:border-amber-400 bg-white text-sm"
                          placeholder="Répéter le mot de passe"
                          minLength={6}
                        />
                      </div>
                      <p className="text-xs text-amber-600 flex items-start gap-1">
                        <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
                        <span>L'utilisateur sera déconnecté et devra se reconnecter avec le nouveau mot de passe.</span>
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handlePasswordChange}
                          disabled={loadingPassword || !newPassword || !confirmPassword}
                          className="px-5 py-2.5 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all font-semibold text-sm disabled:opacity-50 whitespace-nowrap cursor-pointer"
                        >
                          {loadingPassword ? (
                            <><i className="ri-loader-4-line animate-spin mr-1.5"></i>Changement...</>
                          ) : (
                            <><i className="ri-check-line mr-1.5"></i>Appliquer le nouveau mot de passe</>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setShowPasswordSection(false);
                            setNewPassword('');
                            setConfirmPassword('');
                            setPasswordError('');
                          }}
                          className="px-5 py-2.5 bg-white border-2 border-amber-200 text-amber-700 rounded-full hover:bg-amber-100 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: RDV */}
          {activeTab === 'rdv' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Rendez-vous ({rendezVous.length})
              </h3>

              {loadingRdv ? (
                <div className="text-center py-12 text-gray-500">
                  <i className="ri-loader-4-line text-3xl animate-spin mb-3"></i>
                  <div>Chargement des rendez-vous...</div>
                </div>
              ) : rendezVous.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <i className="ri-calendar-line text-4xl mb-3 block"></i>
                  Aucun rendez-vous trouvé
                </div>
              ) : (
                <div className="space-y-3">
                  {rendezVous.map((rdv) => (
                    <div key={rdv.id} className="border-2 border-gray-100 rounded-2xl p-4 hover:border-clinic-blue/20 transition-colors">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-800">
                              {rdv.patients ? `${rdv.patients.prenom} ${rdv.patients.nom}` : 'Patient inconnu'}
                            </span>
                            {rdv.patients?.numero_dossier && (
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                                {rdv.patients.numero_dossier}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            <i className="ri-calendar-line mr-1"></i>
                            {new Date(rdv.date_rdv + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            {' · '}
                            {rdv.heure_debut?.slice(0, 5)} - {rdv.heure_fin?.slice(0, 5)}
                          </div>
                          {rdv.motif && (
                            <p className="text-sm text-gray-600 mt-1">{rdv.motif}</p>
                          )}
                          {rdv.type_rdv && (
                            <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                              {rdv.type_rdv}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUT_COLORS[rdv.statut] || 'bg-gray-100 text-gray-600'}`}>
                            {STATUT_LABELS[rdv.statut] || rdv.statut}
                          </span>
                          {(rdv.statut === 'en_attente' || rdv.statut === 'confirme') && (
                            <button
                              onClick={() => handleCancelRdv(rdv.id)}
                              className="px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-xs font-medium hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Annuler
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab: Dispos */}
          {activeTab === 'dispos' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Gérer les disponibilités</h3>

              {loadingDispos ? (
                <div className="text-center py-12 text-gray-500">
                  <i className="ri-loader-4-line text-3xl animate-spin mb-3"></i>
                  <div>Chargement...</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {JOURS_SEMAINE.map((jour) => {
                    const dispos = getDisposParJour(jour.value);
                    return (
                      <div key={jour.value} className="border-2 border-gray-100 rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-700">{jour.label}</h4>
                          <button
                            onClick={() => handleAddDispo(jour.value)}
                            className="px-3 py-1.5 bg-clinic-blue text-white rounded-full text-xs font-medium hover:bg-clinic-blue-dark transition-all whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-add-line mr-1"></i>Ajouter
                          </button>
                        </div>
                        {dispos.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-2">Aucun créneau</p>
                        ) : (
                          <div className="space-y-2">
                            {dispos.map((dispo) => (
                              <div
                                key={dispo.id}
                                className={`p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                                  dispo.actif ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <button
                                  onClick={() => handleToggleDispo(dispo.id, dispo.actif)}
                                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all cursor-pointer flex-shrink-0 ${
                                    dispo.actif ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 hover:bg-gray-500'
                                  }`}
                                >
                                  <i className={`text-sm text-white ${dispo.actif ? 'ri-check-line' : 'ri-close-line'}`}></i>
                                </button>
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                  <input
                                    type="time"
                                    value={dispo.heure_debut}
                                    onChange={(e) => handleUpdateDispoHeure(dispo.id, 'heure_debut', e.target.value)}
                                    className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-clinic-blue"
                                  />
                                  <input
                                    type="time"
                                    value={dispo.heure_fin}
                                    onChange={(e) => handleUpdateDispoHeure(dispo.id, 'heure_fin', e.target.value)}
                                    className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-clinic-blue"
                                  />
                                </div>
                                <button
                                  onClick={() => handleDeleteDispo(dispo.id)}
                                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-100 text-red-400 hover:text-red-600 transition-all cursor-pointer flex-shrink-0"
                                >
                                  <i className="ri-delete-bin-line text-sm"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}